// The side drawer forms open in (RizzUI Drawer, as in design/isomorphic/src/pages/secret-drawer.tsx):
// a header with a close button, a scrolling body and a footer for the actions. The Secrets page
// keeps its own variant, whose footer names the device that signs the change.
import { X } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { ActionIcon, Drawer } from "rizzui";

import { useT } from "../i18n";
import { cn } from "./cn";

interface Props {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Under the title: what is being edited. */
  subtitle?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  size?: number;
}

export function SideDrawer({ open, onClose, title, subtitle, footer, children, size = 480 }: Props) {
  const t = useT();
  return (
    <Drawer isOpen={open} onClose={onClose} customSize={size} containerClassName="max-w-full dark:bg-gray-50">
      <section className="flex h-full flex-col" aria-label={typeof title === "string" ? title : undefined}>
        <div className="flex items-start justify-between gap-3 border-b border-muted px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg">{title}</h2>
            {subtitle ? <div className="mt-0.5 truncate text-[13px] text-gray-500">{subtitle}</div> : null}
          </div>
          <ActionIcon size="sm" variant="text" onClick={onClose} aria-label={t.common.actions.close}>
            <X weight="bold" className="h-4 w-4" />
          </ActionIcon>
        </div>
        <div className="custom-scrollbar flex-1 space-y-5 overflow-y-auto p-5">{children}</div>
        {footer ? <div className="flex flex-wrap items-center justify-end gap-3 border-t border-muted px-5 py-4">{footer}</div> : null}
      </section>
    </Drawer>
  );
}

/** A labelled native select styled like RizzUI's inputs (forms plugin), testable by its label. */
export function SelectField({
  label,
  value,
  onChange,
  options,
  className,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  className?: string;
  error?: string;
}) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-1.5 block text-sm font-medium text-gray-900">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={cn(
          "h-10 w-full rounded-md border border-gray-300 bg-transparent px-3 text-sm text-gray-900 focus:border-gray-1000 focus:ring-1 focus:ring-gray-1000 dark:bg-gray-50",
          error && "border-red focus:border-red focus:ring-red",
        )}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <span className="mt-1 block text-[13px] text-red">{error}</span> : null}
    </label>
  );
}

/** A quiet hint under a field (RizzUI's helperText is a live region, too loud for a hint). */
export function Hint({ children }: { children: ReactNode }) {
  return <p className="mb-0 mt-1 text-[13px] text-gray-500">{children}</p>;
}
